<template>
    <div class="k-autocomplete" :class="{'k-autocomplete--hide-details': hideValidation}">
        <div v-if="label" class="k-autocomplete__label">{{ label }}</div>
        <v-autocomplete
            v-model="value"
            @input="inputEmitSearchValueAction"
            :search="searchInput"
            :items="getItems"
            :placeholder="placeholder"
            :disabled="disabled"
            :single-line="!variant"
            :variant="variant"
            :clearable="clearable"
            :item-title="itemTitle"
            :item-value="itemValue"
            closable-chips
            :chips="chips"
            :error-messages="errors"
            :multiple="multiple"
            :hint="hint"
            :persistent-hint="persistentHint"
            :loading="loading"
            required
            :no-filter="noFilter"
            :return-object="returnObject"
            :class="{'k-autocomplete__disabled': disabled}"
            :no-data-text="null == noDataText ? $t('common.noDataText') : noDataText"
            @update:modelValue="handleUpdateModelValue"
            ref="autocompleteRef"
            @update:focused="focusAction"
            :menu-icon="`mdi-${menuIcon}`"
        >
            <template v-slot:prepend-item>
                <slot name="prepend-item"></slot>
            </template>
            <template v-slot:prepend-inner>
                <slot name="prepend-inner"></slot>
            </template>
            <template v-slot:prepend>
                <slot name="prepend"></slot>
            </template>
            <template v-slot:append>
                <slot name="append"></slot>
            </template>
            <template v-slot:append-item>
                <slot name="append-item"></slot>
            </template>
            <template v-slot:append-inner>
                <slot name="append-inner"></slot>
            </template>
        </v-autocomplete>
    </div>
</template>

<script setup lang="ts">
import { defineProps, toRef, defineEmits, computed, ref, withDefaults } from 'vue';
import { useField } from 'vee-validate';

interface IOwnProps {
    name?: string
    modelValue?: any
    label?: string
    placeholder?: string
    disabled?: boolean
    variant?: 'filled' | 'outlined' | 'plain' | 'underlined' | 'solo' | 'solo-inverted' | 'solo-filled' | undefined
    clearable?: boolean
    chips?: boolean
    multiple?: boolean
    getItems?: any[]
    loading?: boolean
    itemTitle?: string
    itemValue?: string
    noFilter?: boolean
    returnObject?: boolean
    searchInputModel?: string
    hint?: string
    persistentHint?: boolean
    noDataText?: string | null
    menuIcon?: string
    isFetchHandlerAction?: boolean
    hideValidation?: boolean
}

interface IProps extends IOwnProps {
}

const props = withDefaults(defineProps<IProps>(), {
    menuIcon: 'chevron-down',
});
const emit = defineEmits(['update:modelValue', 'update:search', 'onChange', 'fetchAction', 'focusAction', 'fetchClearAction']);

const { errors, value: fieldValue } = useField(toRef<IOwnProps, any>(props, 'name'), undefined, {
    syncVModel: true
});

const value = computed({
    get() {
        return fieldValue.value !== undefined ? fieldValue.value : props.modelValue;
    },
    set(value) {
        fieldValue.value = value;
        emit('update:modelValue', value);
    }
});

// TO CHECK DOESN'T WORK NOW
const searchInput = computed({
    get() {
        return props.searchInputModel;
    },
    set(value) {
        emit('update:search', value);
    }
});

const inputEmitSearchValueAction = (val:any) => {
    emit('update:search', val.target.value);
};
const autocompleteRef = ref<any>(null);
const handleUpdateModelValue = async() => {
    if (null != autocompleteRef.value) {
        autocompleteRef.value.search = null;
    }
    emit('onChange');
};

const scrollAction = () => {
    let autocompleteContent = document.querySelector('.v-list--one-line');
    if (autocompleteContent && autocompleteContent.scrollTop + autocompleteContent.clientHeight >= autocompleteContent.scrollHeight) {
        emit('fetchAction');
    }
};
const focusAction = () => {
    if (props.isFetchHandlerAction) {
        setTimeout(() => {
            let autocompleteContent = document.querySelector('.v-list--one-line');
            if (null != autocompleteContent) {
                autocompleteContent.addEventListener('scroll', scrollAction);
            } else {
                emit('fetchClearAction');
            }
        }, 100);
    } else {
        emit('focusAction');
    }
};
</script>

<style scoped lang="scss">
.k-autocomplete {
    &__label {
        color: $grey-400;
        font-weight: 700;
        font-size: 14px;
    }

    &__disabled {
        &:deep(.v-input__slot fieldset) {
            border: none;
        }
        &:deep(.v-input__slot) {
            padding: 0 !important;
        }
    }

    &--hide-details {
        &:deep(.v-input__details) {
            display: none;
        }
    }

    &:deep(.v-select.v-select--chips:not(.v-text-field--single-line).v-text-field--enclosed .v-select__selections) {
        min-height: 41px;
    }

    &:deep(.v-input__slot) {
        border-radius: 8px;
    }

    &:deep(.v-input__slot fieldset) {
        border-color: $black;
    }

    &:deep(.v-chip--select) {
        background-color: $secondary-400 !important;
        color: $white !important;
    }

    &:deep(.v-chip__close) {
        color: $black !important;
    }

    &:deep(.v-field--variant-filled .v-field__overlay) {
        background-color: $white;
    }

    &:deep(.v-input--horizontal .v-input__append) {
        margin-inline-start: 0;
        margin-inline-end: 0;
    }

    &:deep(.v-input--horizontal .v-input__prepend) {
        margin-inline-start: 0;
        margin-inline-end: 0;
    }

    &:deep(.v-field__input) {
        padding: 6px 0 6px !important;
        min-height: 41px !important;
    }

    &:deep(.v-field) {
        color: $black;
        font-size: 14px;
        padding: 0 12px 0 17px;
    }

    &:deep(.v-text-field .v-input__details) {
        padding-inline: 0;
    }
}
</style>
